/**
 * 全国态势地图 · 数据 hooks 与聚合
 *
 * 数据来源（全部为 p2-project 已实现的真实接口，不含模拟数据）：
 *   - GET /api/projects/map    地图光点（经纬度 + 健康等级）
 *   - GET /api/projects/stats  首页聚合（按省 / 按状态 / 按健康等级）
 *   - GET /api/projects        列表（healthScore / overdueTaskCnt 等，补充地图接口没有的字段）
 *   - GET /api/tasks           任务（任务数、指标达标率、任务形态）
 *   - GET /api/approvals       审批（积压）
 *   - GET /geo/china.json      中国省级行政区 GeoJSON（静态资源，非后端接口）
 *
 * ⚠️ 后端没有的指标一律不显示，绝不用模拟数据补齐（详见回执）。
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { healthLevelOf, TASK_FORM_LABELS } from '@atlaspm/shared';
import { api, qs } from '../../api/client';
import { activeProjectTypeApi } from '../project/api';
import { qk } from '../../api/queryKeys';
import type { Paginated } from '../../api/types';
import type { ChinaGeo, Position } from './projection';
import type { ProjectHealthLevel } from './colors';

/* ═══════════════════════════════════════════════════════════
   接口返回结构（以 curl 实测为准）
   ═══════════════════════════════════════════════════════════ */

export interface MapMarker {
  id: string;
  code: string;
  name: string;
  provinceCode: string;
  provinceName: string;
  longitude: number;
  latitude: number;
  healthLevel: ProjectHealthLevel;
  progressPct: number;
  status: string;
  projectType: string;
}

export interface StatsByProvince {
  provinceCode: string;
  provinceName: string;
  count: number;
  avgProgress: number;
  avgHealth: number;
}

export interface ProjectStatsData {
  totalProjects: number;
  byStatus: Record<string, number>;
  byHealthLevel: Record<string, number>;
  avgProgress: number;
  overdueProjects: number;
  byProvince: StatsByProvince[];
}

export interface ProjectListItemData {
  id: string;
  code: string;
  name: string;
  shortName?: string | null;
  projectType: string;
  provinceCode: string;
  provinceName: string;
  longitude: number;
  latitude: number;
  status: string;
  healthScore: number;
  progressPct: number;
  overdueTaskCnt: number;
  ownerId?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  isSecret: boolean;
  createdAt: string;
  /** ⭐ P6.54 项目齐套率（%）；无「需附件要素」时为 null */
  deliverableReadyPct?: number | null;
  deliverableRequiredCnt?: number;
  deliverableSatisfiedCnt?: number;
}

export interface TaskListItemData {
  id: string;
  projectId: string;
  projectName?: string;
  code: string;
  name: string;
  categoryId?: string | null;
  categoryName?: string | null;
  ownerId: string;
  ownerName?: string | null;
  state: string;
  progressPct: number;
  metricTotal: number;
  metricAchieved: number;
  isMilestone: boolean;
  priority: number;
  planStart?: string | null;
  planEnd?: string | null;
  createdAt: string;
}

export interface ApprovalListItemData {
  id: string;
  status: string;
  taskId?: string;
  projectId?: string;
  /** 审批发起时间（计算「最长等待」用） */
  startedAt?: string | null;
}

/* ═══════════════════════════════════════════════════════════
   派生视图模型
   ═══════════════════════════════════════════════════════════ */

/** 地图项目（地图光点 + 列表补充字段 + 任务聚合） */
export interface MapProject extends MapMarker {
  /**
   * 派生健康等级（决定光点颜色）。
   *
   * ⚠️ 后端 `healthLevel` 会把所有 PREPARING（未正式启动）项目一律标为 IDLE，
   *    哪怕它已有 healthScore。若直接采用，地图将全是灰点，违背「3 秒看出哪里有问题」。
   *    因此这里按任务书 5.2 的口径，用 healthScore 分档：
   *      - FROZEN 保留（冻结是数据态）
   *      - healthScore ≤ 0 且未启动 → IDLE
   *      - 其余 → healthLevelOf(healthScore)（≥85 绿 / 60~85 琥珀 / <60 玫红）
   */
  level: ProjectHealthLevel;
  /** 来自 /projects 列表；IDLE / FROZEN 时为 0 */
  healthScore: number;
  overdueTaskCnt: number;
  taskCount: number;
  metricTotal: number;
  metricAchieved: number;
  /** 指标达标率（0~1），无指标时为 null */
  metricPassRate: number | null;
  ownerId?: string | null;
  /** ⭐ P6.54 项目齐套率（%）；无「需附件要素」时为 null */
  deliverableReadyPct: number | null;
  deliverableRequiredCnt: number;
  deliverableSatisfiedCnt: number;
}

/** 省份聚合（排行 / 地图标签 / 下钻 KPI 用） */
export interface ProvinceAgg {
  provinceCode: string;
  provinceName: string;
  count: number;
  avgHealth: number;
  avgProgress: number;
  overdueCount: number;
  taskCount: number;
  metricTotal: number;
  metricAchieved: number;
  metricPassRate: number | null;
  level: ProjectHealthLevel;
  /** 省名标签定位（取自 GeoJSON properties.center） */
  center: Position | null;
}

export type KpiTone = 'cy' | 'good' | 'warn' | 'bad';

export interface KpiItem {
  key: string;
  label: string;
  value: string;
  unit?: string;
  desc?: string;
  tone: KpiTone;
  /**
   * P6.29：可下钻目标路由。
   *
   * ⚠️ 仅设置此项的 KPI 才可点击（当前只有「审批积压」→ /approvals）。
   *    未设置者渲染为纯展示卡，避免误以为可点。
   */
  to?: string;
}

/* ═══════════════════════════════════════════════════════════
   基础查询
   ═══════════════════════════════════════════════════════════ */

export function useMapMarkers() {
  return useQuery({
    queryKey: qk.projectMap(),
    queryFn: () => api.get<MapMarker[]>('/projects/map'),
  });
}

export function useProjectStats() {
  return useQuery({
    queryKey: qk.projectStats(),
    queryFn: () => api.get<ProjectStatsData>('/projects/stats'),
  });
}

/** 单页最大条数（后端 ValidationPipe 限制 pageSize ≤ 200） */
const PAGE_SIZE = 200;

export function useProjectList() {
  return useQuery({
    queryKey: qk.projects({ all: true }),
    queryFn: async (): Promise<Paginated<ProjectListItemData>> => {
      const first = await api.get<Paginated<ProjectListItemData>>(
        `/projects${qs({ page: 1, pageSize: PAGE_SIZE })}`,
      );
      if (first.total <= PAGE_SIZE) return first;
      const items = [...first.items];
      const pages = Math.ceil(first.total / PAGE_SIZE);
      for (let page = 2; page <= pages; page++) {
        const next = await api.get<Paginated<ProjectListItemData>>(
          `/projects${qs({ page, pageSize: PAGE_SIZE })}`,
        );
        items.push(...next.items);
      }
      return { ...first, items };
    },
  });
}

/**
 * 拉取**全部**任务（自动翻页）。
 *
 * ⚠️ 后端单页上限 200，演示数据 221 条任务。若只取第一页，
 *    「已冻结」计数、指标汇总、任务形态分布都会漏数，故这里必须翻页取全。
 */
export function useTaskList() {
  return useQuery({
    queryKey: qk.tasks({ all: true }),
    queryFn: async (): Promise<Paginated<TaskListItemData>> => {
      const first = await api.get<Paginated<TaskListItemData>>(
        `/tasks${qs({ page: 1, pageSize: PAGE_SIZE, sortBy: 'priority', sortOrder: 'asc' })}`,
      );
      if (first.total <= PAGE_SIZE) return first;
      const items = [...first.items];
      const pages = Math.ceil(first.total / PAGE_SIZE);
      for (let page = 2; page <= pages; page++) {
        const next = await api.get<Paginated<TaskListItemData>>(
          `/tasks${qs({ page, pageSize: PAGE_SIZE, sortBy: 'priority', sortOrder: 'asc' })}`,
        );
        items.push(...next.items);
      }
      return { ...first, items };
    },
  });
}

/** 逾期任务总数（后端按 plan_end < today 且未完成 判定） */
export function useOverdueTaskTotal() {
  return useQuery({
    queryKey: qk.tasks({ overdueOnly: true }),
    queryFn: () =>
      api.get<Paginated<TaskListItemData>>(`/tasks${qs({ page: 1, pageSize: 1, overdueOnly: 'true' })}`),
  });
}

export function useApprovalList() {
  return useQuery({
    queryKey: qk.approvals({ pageSize: PAGE_SIZE }),
    queryFn: () => api.get<Paginated<ApprovalListItemData>>(`/approvals${qs({ page: 1, pageSize: PAGE_SIZE })}`),
  });
}

/**
 * 项目类型字典（公开只读 `GET /project-types`）。
 *
 * ⭐ P6.37：地图页需要把项目上存的 `code`（如 `PT_MUXD3SMN2NSN`）映射为中文名。
 *    此前地图页只调 `useMapData()`，从不触发 `useProjectTypes()` 的副作用，
 *    导致 `effectiveProjectTypes()` 恒回退硬编码常量 ⇒ 查不到新字典 code ⇒ 显示 code。
 *    这里直接在地图页拉字典，不再依赖全局可变缓存。
 *
 * ⚠️ queryKey 与 `useProjectTypes()` 完全一致（`['project-types','active']`），
 *    共享 TanStack 缓存，不会重复请求。
 */
export function useActiveProjectTypes() {
  return useQuery({
    queryKey: ['project-types', 'active'] as const,
    queryFn: activeProjectTypeApi.list,
    staleTime: 30 * 60 * 1000,
    gcTime: 24 * 60 * 60 * 1000,
    retry: 1,
  });
}

/** 中国省级 GeoJSON（静态资源） */
export function useChinaGeo() {
  return useQuery({
    queryKey: ['geo', 'china'],
    queryFn: async (): Promise<ChinaGeo> => {
      const res = await fetch('/geo/china.json');
      if (!res.ok) throw new Error(`中国地图数据加载失败（HTTP ${res.status}）`);
      return (await res.json()) as ChinaGeo;
    },
    staleTime: Infinity,
    gcTime: Infinity,
  });
}

/* ═══════════════════════════════════════════════════════════
   聚合
   ═══════════════════════════════════════════════════════════ */

interface TaskAgg {
  taskCount: number;
  metricTotal: number;
  metricAchieved: number;
}

function aggregateTasks(tasks: readonly TaskListItemData[]): Map<string, TaskAgg> {
  const byProject = new Map<string, TaskAgg>();
  for (const t of tasks) {
    const agg = byProject.get(t.projectId) ?? { taskCount: 0, metricTotal: 0, metricAchieved: 0 };
    agg.taskCount += 1;
    agg.metricTotal += t.metricTotal ?? 0;
    agg.metricAchieved += t.metricAchieved ?? 0;
    byProject.set(t.projectId, agg);
  }
  return byProject;
}

/** 把地图光点 + 列表字段 + 任务聚合合并成 MapProject[] */
export function mergeProjects(
  markers: readonly MapMarker[],
  listItems: readonly ProjectListItemData[],
  tasks: readonly TaskListItemData[],
): MapProject[] {
  const listById = new Map(listItems.map((p) => [p.id, p]));
  const taskAgg = aggregateTasks(tasks);

  return markers.map((m) => {
    const li = listById.get(m.id);
    const agg = taskAgg.get(m.id);
    const metricTotal = agg?.metricTotal ?? 0;
    const metricAchieved = agg?.metricAchieved ?? 0;
    const healthScore = li?.healthScore ?? 0;
    return {
      ...m,
      level: deriveLevel(m, healthScore),
      healthScore,
      overdueTaskCnt: li?.overdueTaskCnt ?? 0,
      ownerId: li?.ownerId ?? null,
      taskCount: agg?.taskCount ?? 0,
      metricTotal,
      metricAchieved,
      metricPassRate: metricTotal > 0 ? metricAchieved / metricTotal : null,
      deliverableReadyPct: li?.deliverableReadyPct ?? null,
      deliverableRequiredCnt: li?.deliverableRequiredCnt ?? 0,
      deliverableSatisfiedCnt: li?.deliverableSatisfiedCnt ?? 0,
    };
  });
}

/** 派生健康等级（见 MapProject.level 注释） */
function deriveLevel(m: MapMarker, healthScore: number): ProjectHealthLevel {
  if (m.healthLevel === 'FROZEN') return 'FROZEN';
  if (healthScore <= 0) return 'IDLE';
  return healthLevelOf(healthScore);
}

/** 省聚合：以 stats.byProvince 为权威，叠加光点/项目/任务补充信息 */
export function aggregateProvinces(
  stats: ProjectStatsData | undefined,
  projects: readonly MapProject[],
  geo: ChinaGeo | undefined,
): ProvinceAgg[] {
  const centerByCode = new Map<string, Position>();
  if (geo) {
    for (const f of geo.features) {
      const code = String(f.properties.adcode ?? '');
      const center = f.properties.center ?? f.properties.centroid;
      if (center && /^\d{2}0000$/.test(code)) centerByCode.set(code, center);
    }
  }

  const byProvinceMarkers = new Map<string, MapProject[]>();
  for (const p of projects) {
    const arr = byProvinceMarkers.get(p.provinceCode) ?? [];
    arr.push(p);
    byProvinceMarkers.set(p.provinceCode, arr);
  }

  const rows = stats?.byProvince ?? [];
  return rows.map((bp) => {
    const markers = byProvinceMarkers.get(bp.provinceCode) ?? [];
    const overdueCount = markers.reduce((s, m) => s + (m.overdueTaskCnt ?? 0), 0);
    const taskCount = markers.reduce((s, m) => s + m.taskCount, 0);
    const metricTotal = markers.reduce((s, m) => s + m.metricTotal, 0);
    const metricAchieved = markers.reduce((s, m) => s + m.metricAchieved, 0);
    const level = provinceLevel(markers, bp.avgHealth);
    return {
      provinceCode: bp.provinceCode,
      provinceName: bp.provinceName,
      count: bp.count,
      avgHealth: bp.avgHealth,
      avgProgress: bp.avgProgress,
      overdueCount,
      taskCount,
      metricTotal,
      metricAchieved,
      metricPassRate: metricTotal > 0 ? metricAchieved / metricTotal : null,
      level,
      center: centerByCode.get(bp.provinceCode) ?? null,
    };
  });
}

/** 省健康等级：全省项目同为未启动/冻结则沿用，否则按平均健康度分档 */
function provinceLevel(markers: readonly MapProject[], avgHealth: number): ProjectHealthLevel {
  const levels = new Set(markers.map((m) => m.level));
  if (levels.size === 1) {
    const only = [...levels][0];
    if (only === 'IDLE' || only === 'FROZEN') return only;
  }
  return healthLevelOf(avgHealth);
}

/* ═══════════════════════════════════════════════════════════
   KPI
   ═══════════════════════════════════════════════════════════ */

/**
 * 全国 7 张 KPI 卡（对齐原型 `renderKPI()` 的全国分支）。
 *
 * 文案与色调逐字对照原型：
 *   项目数量 / 全国健康度 / 任务总数 / 逾期任务 / 指标达标率 / 产出物齐套率 / 审批积压
 *
 * ⚠️ 入参 `projects` 是**当前筛选后**的项目集合，筛选时 KPI 随之变化。
 * ⭐ P6.54：产出物齐套率接入真实数据——口径为「项目下已上传附件的需附件要素数 /
 *    需附件要素总数」，由 project 聚合字段（ProjectAggregateService）维护，
 *    与任务级 / 星图页同口径；无任何「需附件要素」时显示 `—`（不是 0%）。
 */
export function buildNationalKpis(args: {
  projects: readonly MapProject[];
  tasks: readonly TaskListItemData[];
  approvals: readonly ApprovalListItemData[];
  /** 注入当前时间戳，便于测试与「审批超时」判定 */
  now?: number;
}): KpiItem[] {
  const { projects, tasks, approvals, now = Date.now() } = args;

  const projectIds = new Set(projects.map((p) => p.id));
  const scopedTasks = tasks.filter((t) => projectIds.has(t.projectId));

  const provinceCount = new Set(projects.map((p) => p.provinceCode)).size;
  const projectCount = projects.length;

  const active = projects.filter((p) => p.level !== 'IDLE' && p.level !== 'FROZEN');
  const avgHealth =
    active.length > 0 ? active.reduce((s, p) => s + p.healthScore, 0) / active.length : null;

  const taskCount = scopedTasks.length;
  const frozenCount = scopedTasks.filter((t) => t.state === 'FROZEN').length;

  const overdueCount = projects.reduce((s, p) => s + (p.overdueTaskCnt ?? 0), 0);
  const overdueRate = taskCount > 0 ? (overdueCount / taskCount) * 100 : 0;

  let metricTotal = 0;
  let metricAchieved = 0;
  for (const t of scopedTasks) {
    metricTotal += t.metricTotal ?? 0;
    metricAchieved += t.metricAchieved ?? 0;
  }
  const metricRate = metricTotal > 0 ? (metricAchieved / metricTotal) * 100 : null;

  // ⭐ P6.54 产出物齐套率：按当前可见（筛选后）项目的需附件要素汇总
  const deliverableRequired = projects.reduce((s, p) => s + (p.deliverableRequiredCnt ?? 0), 0);
  const deliverableSatisfied = projects.reduce((s, p) => s + (p.deliverableSatisfiedCnt ?? 0), 0);
  const deliverableRate =
    deliverableRequired > 0 ? (deliverableSatisfied / deliverableRequired) * 100 : null;

  const backlogItems = approvals.filter(
    (a) =>
      a.projectId !== undefined &&
      projectIds.has(a.projectId) &&
      (a.status === 'PENDING' || a.status === 'APPROVING'),
  );
  const backlog = backlogItems.length;
  const backlogOverdue = backlogItems.filter((a) => {
    if (!a.startedAt) return false;
    const t = Date.parse(a.startedAt);
    return !Number.isNaN(t) && now - t > 24 * 3_600_000;
  }).length;

  return [
    {
      key: 'provinces',
      label: '项目数量',
      value: String(projectCount),
      unit: '个',
      desc: `覆盖 ${provinceCount} 个省份`,
      tone: 'cy',
    },
    {
      key: 'health',
      label: '全国健康度',
      value: avgHealth === null ? '—' : avgHealth.toFixed(1),
      desc: '加权平均',
      tone: avgHealth === null ? 'cy' : toneOfHealth(avgHealth),
    },
    {
      key: 'tasks',
      label: '任务总数',
      value: String(taskCount),
      unit: '个',
      desc: `已冻结 ${frozenCount}`,
      tone: 'cy',
    },
    {
      key: 'overdue',
      label: '逾期任务',
      value: String(overdueCount),
      unit: '个',
      desc: `占比 ${overdueRate.toFixed(1)}%`,
      tone: 'bad',
    },
    {
      key: 'metric-pass',
      label: '指标达标率',
      value: metricRate === null ? '—' : metricRate.toFixed(1),
      unit: metricRate === null ? undefined : '%',
      desc: `${metricTotal} 个指标`,
      tone: metricRate === null ? 'cy' : metricRate >= 80 ? 'good' : 'warn',
    },
    {
      key: 'deliverable',
      label: '产出物齐套率',
      value: deliverableRate === null ? '—' : deliverableRate.toFixed(1),
      unit: deliverableRate === null ? undefined : '%',
      desc: `${deliverableRequired} 个需附件要素`,
      tone: deliverableRate === null ? 'cy' : deliverableRate >= 80 ? 'good' : 'warn',
    },
    {
      key: 'approval',
      label: '审批积压',
      value: String(backlog),
      unit: '个',
      desc: `超时 ${backlogOverdue} 个`,
      tone: backlog > 30 ? 'warn' : 'good',
      /* P6.29：可直接下钻到审批工作台（积压为 0 也可点，便于回看历史审批记录）
         ⭐ P6.52：链接携带 tab=pending，下钻后默认落「全部待审批」，
         与 KPI 口径一致，避免点进去空白或数字对不上。 */
      to: '/approvals?tab=pending',
    },
  ];
}

function toneOfHealth(score: number): KpiTone {
  const level = healthLevelOf(score);
  if (level === 'HEALTHY') return 'good';
  if (level === 'WARNING') return 'warn';
  return 'bad';
}

/* ═══════════════════════════════════════════════════════════
   汇总 hook
   ═══════════════════════════════════════════════════════════ */

export interface MapDataResult {
  geo: ChinaGeo | undefined;
  projects: MapProject[];
  provinces: ProvinceAgg[];
  markers: MapMarker[];
  tasks: TaskListItemData[];
  tasksTotal: number;
  overdueTotal: number;
  approvals: ApprovalListItemData[];
  /** 项目类型选项（从真实项目数据动态取，值即后端 enum code） */
  projectTypes: string[];
  /**
   * 项目类型字典：code → 中文名（来自 `GET /project-types`）。
   * 空对象 = 字典未加载 / 接口失败（调用方需回退 `projectTypeLabel()`）。
   */
  projectTypeLabels: Record<string, string>;
  /** 任务形态选项（从真实任务的 categoryName 动态取，按内置 8 形态顺序排序） */
  taskForms: string[];
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

export function useMapData(): MapDataResult {
  const geoQ = useChinaGeo();
  const markersQ = useMapMarkers();
  const statsQ = useProjectStats();
  const listQ = useProjectList();
  const tasksQ = useTaskList();
  const overdueQ = useOverdueTaskTotal();
  const approvalsQ = useApprovalList();
  const projectTypesQ = useActiveProjectTypes();

  const markers = useMemo(() => markersQ.data ?? [], [markersQ.data]);
  const tasks = useMemo(() => tasksQ.data?.items ?? [], [tasksQ.data]);
  const projects = useMemo(
    () => mergeProjects(markers, listQ.data?.items ?? [], tasks),
    [markers, listQ.data, tasks],
  );
  const provinces = useMemo(
    () => aggregateProvinces(statsQ.data, projects, geoQ.data),
    [statsQ.data, projects, geoQ.data],
  );

  const approvals = useMemo(() => approvalsQ.data?.items ?? [], [approvalsQ.data]);
  const tasksTotal = tasksQ.data?.total ?? tasks.length;
  const overdueTotal = overdueQ.data?.total ?? 0;

  const projectTypes = useMemo(
    () => [...new Set(projects.map((p) => p.projectType).filter(Boolean))].sort(),
    [projects],
  );

  /** code → 中文名（字典未加载 / 接口失败时为空对象） */
  const projectTypeLabels = useMemo(() => {
    const map: Record<string, string> = {};
    for (const t of projectTypesQ.data ?? []) map[t.code] = t.name;
    return map;
  }, [projectTypesQ.data]);

  const taskForms = useMemo(() => {
    const set = new Set<string>();
    for (const t of tasks) if (t.categoryName) set.add(t.categoryName);
    const order = Object.values(TASK_FORM_LABELS);
    return [...set].sort((a, b) => {
      const ia = order.indexOf(a);
      const ib = order.indexOf(b);
      return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
    });
  }, [tasks]);

  const isLoading =
    geoQ.isLoading ||
    markersQ.isLoading ||
    statsQ.isLoading ||
    listQ.isLoading ||
    tasksQ.isLoading ||
    projectTypesQ.isLoading;
  const isError = markersQ.isError || statsQ.isError;
  const error = markersQ.error ?? statsQ.error;

  return {
    geo: geoQ.data,
    projects,
    provinces,
    markers,
    tasks,
    tasksTotal,
    overdueTotal,
    approvals,
    projectTypes,
    projectTypeLabels,
    taskForms,
    isLoading,
    isError,
    error,
  };
}
